import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, BookOpen, CalendarPlus, Check, ClipboardCopy, Heart, MapPin, MessageCircle, Minus, Plus, Share2, Ship, Sparkles, Star } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Textarea, SubmitButton, CtaBand, usePageMeta } from "@/components/shared";
import { api, submitLead, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/site";
import { GUIDES } from "@/lib/guides";
import { PLAN_PATHS, CORE_QUESTIONS, PATH_QUESTIONS, CONCIERGE_SERVICES, INTEREST_OPTIONS, scoreRecommendations, buildItinerary, tripDays, hoursAshore, priceLabel, calendarLink } from "@/lib/planner";
import { TripMap } from "@/components/TripMap";

const PATH_GUIDE = { general: "guest-guide", couples: "couples-guide", kids: "kids-guide", luxury: "luxury-guide", cruise: "cruise-guide" };

const GUIDE_PITCH = {
  "guest-guide": "Pair your plan with the complete guest guide — 16 fillable pages for trip details, guest lists, itineraries and island know-how.",
  "couples-guide": "Keep the romance organized — date nights, sunset spots and celebrations, planned beautifully on paper.",
  "kids-guide": "The family planner — age-aware activities, beach days and rainy-day backups, with space for every child's list.",
  "luxury-guide": "Your elevated escape, organized — charters, tables, spa days and curated island moments.",
  "cruise-guide": "Never miss the ship — dock details, timed port-day itineraries and all-aboard planning in one fillable guide.",
};

const GuideUpsell = ({ path }) => {
  const guide = GUIDES.find((g) => g.slug === PATH_GUIDE[path]) || GUIDES.find((g) => g.slug === "guest-guide");
  return (
    <div className="flex flex-col gap-5 border border-bermuda/40 bg-pink-soft/40 p-6 sm:flex-row sm:items-center" data-testid="guide-upsell">
      <img src={guide.cover} alt={`${guide.title} — ebook cover`} className="w-24 shrink-0 border border-navy/10 object-cover shadow-lg shadow-navy/10" />
      <div className="flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-bermuda-dark">Organize Your Stay Next</p>
        <h3 className="mt-1.5 font-serif text-xl text-navy">{guide.title}</h3>
        <p className="mt-1.5 text-[13px] leading-relaxed text-navy/60">{GUIDE_PITCH[guide.slug]}</p>
      </div>
      <div className="shrink-0 sm:text-right">
        <p className="font-serif text-2xl font-light text-navy">${guide.price}</p>
        <Link to={`/guides?buy=${guide.slug}`} data-testid="guide-upsell-btn" className="mt-2 inline-block bg-navy px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
          Get the Guide
        </Link>
        <p className="mt-2 text-[10px] text-navy/45">Instant PDF download</p>
      </div>
    </div>
  );
};

const track = (event, props) => { try { window.posthog?.capture(event, props); } catch { /* optional */ } };

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors ${active ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy/60 hover:border-navy/50 hover:text-navy"}`}
  >
    {children}
  </button>
);

const QuestionBlock = ({ q, value, onChange }) => {
  if (q.type === "date" || q.type === "time" || q.type === "text") {
    return (
      <Field label={q.label}>
        <Input data-testid={`plan-q-${q.id}`} type={q.type === "text" ? "text" : q.type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={q.placeholder} />
      </Field>
    );
  }
  if (q.type === "choice") {
    return (
      <div data-testid={`plan-q-${q.id}`}>
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">{q.label}</p>
        <div className="flex flex-wrap gap-2">
          {q.options.map(([v, label]) => (
            <Chip key={v} testid={`plan-q-${q.id}-${v}`} active={value === v} onClick={() => onChange(v)}>{label}</Chip>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "multi") {
    const arr = value || [];
    return (
      <div data-testid={`plan-q-${q.id}`}>
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">{q.label}</p>
        <div className="flex flex-wrap gap-2">
          {q.options.map(([v, label]) => (
            <Chip
              key={v}
              testid={`plan-q-${q.id}-${v}`}
              active={arr.includes(v)}
              onClick={() => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v])}
            >
              {label}
            </Chip>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "party") {
    const party = value || { adults: 2, teens: 0, children: 0, toddlers: 0 };
    const rows = [["adults", "Adults", "18+"], ["teens", "Teens", "13–17"], ["children", "Children", "5–12"], ["toddlers", "Toddlers", "0–4"]];
    return (
      <div data-testid={`plan-q-${q.id}`}>
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">{q.label}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {rows.map(([k, label, hint]) => (
            <div key={k} className="border border-navy/10 bg-sand-light/60 p-3 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-navy/60">{label}</p>
              <p className="text-[9px] text-navy/40">{hint}</p>
              <div className="mt-2 flex items-center justify-center gap-3">
                <button type="button" data-testid={`party-${k}-minus`} onClick={() => onChange({ ...party, [k]: Math.max(k === "adults" ? 1 : 0, (party[k] || 0) - 1) })} className="flex h-7 w-7 items-center justify-center border border-navy/20 text-navy/60 hover:bg-navy hover:text-ivory"><Minus size={12} /></button>
                <span className="w-5 font-serif text-xl text-navy" data-testid={`party-${k}-count`}>{party[k] || 0}</span>
                <button type="button" data-testid={`party-${k}-plus`} onClick={() => onChange({ ...party, [k]: (party[k] || 0) + 1 })} className="flex h-7 w-7 items-center justify-center border border-navy/20 text-navy/60 hover:bg-navy hover:text-ivory"><Plus size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const RecCard = ({ rec, saved, onToggle, answers, index }) => (
  <div className="flex h-full flex-col border border-navy/10 bg-white" data-testid={`rec-card-${rec.id}`}>
    <div className="img-frame relative h-44 overflow-hidden">
      <img src={IMAGES[rec.image] || IMAGES.beach} alt={rec.name} className="h-full w-full object-cover" loading="lazy" />
      <span className="absolute left-3 top-3 bg-navy/85 px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-ivory">{rec.category}</span>
      <span className="absolute right-3 top-3 bg-ivory/90 px-2.5 py-1 text-[10px] font-bold text-navy">{priceLabel(rec.price_level)}</span>
    </div>
    <div className="flex flex-1 flex-col p-5">
      <h3 className="font-serif text-lg leading-snug text-navy">{rec.name}</h3>
      <p className="mt-1 flex items-center gap-1.5 text-[11px] text-navy/50"><MapPin size={11} /> {rec.parish} · ~{rec.duration_hours}h</p>
      <p className="mt-2.5 flex-1 text-[13px] leading-relaxed text-navy/60">{rec.description}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {rec.rainy_day && <span className="bg-ocean-light px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ocean">Rainy day</span>}
        {rec.beach_day && <span className="bg-bermuda/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bermuda-dark">Beach day</span>}
        {(rec.interests || []).slice(0, 3).map((i) => (
          <span key={i} className="bg-sand px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-navy/55">{(INTEREST_OPTIONS.find(([v]) => v === i) || [i, i])[1]}</span>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2">
        <button
          onClick={onToggle}
          data-testid={`rec-save-${rec.id}`}
          className={`flex items-center justify-center gap-1.5 px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${saved ? "bg-bermuda text-navy" : "bg-navy text-ivory hover:bg-bermuda-dark"}`}
        >
          {saved ? <><Check size={12} /> In My Trip</> : <><Plus size={12} /> Add to My Trip</>}
        </button>
        <Link
          to={calendarLink(rec, answers, rec.category === "dining" ? "dining" : "activity")}
          data-testid={`rec-calendar-${rec.id}`}
          className="flex items-center justify-center gap-1.5 border border-navy/25 px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-ivory"
        >
          <CalendarPlus size={12} /> To Calendar
        </Link>
        <a href={rec.maps_url} target="_blank" rel="noopener noreferrer" data-testid={`rec-map-${rec.id}`} className="flex items-center justify-center gap-1.5 border border-navy/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy/60 transition-colors hover:border-navy hover:text-navy">
          <MapPin size={12} /> Map
        </a>
        {rec.booking_url ? (
          <a href={rec.booking_url} target="_blank" rel="noopener noreferrer" data-testid={`rec-book-${rec.id}`} className="flex items-center justify-center gap-1.5 bg-bermuda px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-ivory">
            <Star size={12} /> Book
          </a>
        ) : rec.website ? (
          <a href={rec.website} target="_blank" rel="noopener noreferrer" data-testid={`rec-website-${rec.id}`} className="flex items-center justify-center gap-1.5 border border-navy/15 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy/60 transition-colors hover:border-navy hover:text-navy">
            <ArrowUpRight size={12} /> Website
          </a>
        ) : <span />}
      </div>
    </div>
  </div>
);

export default function Plan() {
  usePageMeta("My Bermuda Stay — Plan Your Trip", "Plan it. Personalize it. Experience it. Tell us about your trip and get personal Bermuda recommendations, a day-by-day itinerary and concierge help from The Bermuda Co-Host.");
  const [params] = useSearchParams();
  const [step, setStep] = useState("path");
  const [path, setPath] = useState(null);
  const [answers, setAnswers] = useState({});
  const [recs, setRecs] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [tripCode, setTripCode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [concierge, setConcierge] = useState({ services: [], name: "", email: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [tripView, setTripView] = useState("list");
  const [weekTab, setWeekTab] = useState(0);

  useEffect(() => {
    track("planner_view");
    const code = params.get("trip");
    if (code) {
      api.get(`/trips/${code}`)
        .then((res) => {
          setPath(res.data.path);
          setAnswers(res.data.answers || {});
          setSavedIds(res.data.saved_ids || []);
          setTripCode(res.data.id);
          setTripView("map");
          setStep("trip");
          toast.success("Welcome back — your trip is loaded.");
        })
        .catch(() => toast.error("We couldn't find that trip link."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!recs) {
      api.get("/recommendations").then((res) => setRecs(res.data)).catch(() => setRecs([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scored = useMemo(() => (recs && path ? scoreRecommendations(recs, answers, path) : []), [recs, answers, path]);
  const saved = useMemo(() => scored.filter((r) => savedIds.includes(r.id)), [scored, savedIds]);
  const itinerary = useMemo(
    () => buildItinerary(saved, tripDays(answers), path === "cruise" ? answers : null),
    [saved, answers, path]
  );
  const cruiseHours = path === "cruise" ? hoursAshore(answers) : null;

  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < itinerary.length; i += 7) w.push(itinerary.slice(i, i + 7));
    return w.length ? w : [[]];
  }, [itinerary]);

  const shareTripText = "My Bermuda trip plan — take a look and add your own ideas:";
  const whatsappTripUrl = tripCode ? `https://wa.me/?text=${encodeURIComponent(`${shareTripText} https://thebermudacohost.com/plan?trip=${tripCode}`)}` : "#";
  const shareTripNative = async () => {
    if (!tripLink) return;
    track("share_trip_click");
    if (navigator.share) {
      try { await navigator.share({ title: "My Bermuda Stay", text: shareTripText, url: tripLink }); } catch { /* dismissed */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareTripText} ${tripLink}`);
        toast.success("Trip link copied — paste it anywhere to share.");
      } catch {
        toast.error("Copy failed — please copy the link manually.");
      }
    }
  };

  const setAnswer = (k) => (v) => setAnswers((a) => ({ ...a, [k]: v }));

  const choosePath = (id) => {
    setPath(id);
    setAnswers(id === "cruise" ? { party: { adults: 2, teens: 0, children: 0, toddlers: 0 } } : { party: { adults: 2, teens: 0, children: 0, toddlers: 0 } });
    setStep("questions");
    track("path_selected");
  };

  const toggleSave = (id) => {
    setSavedIds((s) => {
      const next = s.includes(id) ? s.filter((x) => x !== id) : [...s, id];
      if (!s.includes(id)) track("item_saved");
      return next;
    });
  };

  const saveTrip = async () => {
    setSaving(true);
    try {
      const body = { path, answers, saved_ids: savedIds, itinerary };
      if (tripCode) {
        await api.put(`/trips/${tripCode}`, body);
      } else {
        const res = await api.post("/trips", body);
        setTripCode(res.data.id);
      }
      track("trip_saved");
      toast.success(tripCode ? "Trip updated." : "Trip saved — keep your personal link safe.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendConcierge = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitLead("concierge_request", {
        ...concierge,
        services: concierge.services,
        path,
        saved_places: saved.map((r) => r.name).join(", "),
        trip_code: tripCode || "",
        staying: answers.staying || "",
        arrival: answers.arrival || "",
        departure: answers.departure || "",
      });
      setDone(true);
      track("concierge_request_submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const questions = [...(PATH_QUESTIONS[path] || []), ...CORE_QUESTIONS];
  const tripLink = tripCode ? `https://thebermudacohost.com/plan?trip=${tripCode}` : null;

  return (
    <main data-testid="plan-page">
      <section className="bg-sand-light">
        <div className="mx-auto max-w-[1440px] px-6 pb-14 pt-40 lg:px-10">
          <SectionHead
            overline="My Bermuda Stay"
            title={<>Plan it. Personalize it.<br /><span className="italic text-bermuda-dark">Experience it.</span></>}
            copy="Tell us about your trip and we'll match you with Bermuda's beaches, tables and experiences — saved to your own day-by-day plan, with our concierge one tap away."
            align="center"
          />
          {step !== "path" && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em]" data-testid="plan-steps">
              {[["questions", "Your Trip"], ["recs", "Recommendations"], ["trip", "My Itinerary"], ["concierge", "Concierge"]].map(([k, label]) => (
                <span key={k} className={`px-3 py-1.5 ${step === k ? "bg-navy text-ivory" : "bg-navy/5 text-navy/45"}`}>{label}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-10">
        {step === "path" && (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="plan-paths">
            {PLAN_PATHS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.07} className="h-full">
                <button onClick={() => choosePath(p.id)} data-testid={`plan-path-${p.id}`} className="group flex h-full w-full flex-col border border-navy/10 bg-white text-left transition-all duration-500 hover:border-bermuda hover:shadow-2xl hover:shadow-navy/10">
                  <div className="img-frame aspect-[3/4] overflow-hidden">
                    <img src={p.cover} alt={`${p.title} guide cover`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-serif text-lg leading-snug text-navy">{p.title}</h3>
                    <p className="mt-1.5 flex-1 text-[12px] leading-relaxed text-navy/55">{p.copy}</p>
                    <span className="mt-4 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/60 transition-colors group-hover:text-bermuda-dark">
                      Start Here <ArrowUpRight size={12} />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}

        {step === "questions" && path && (
          <Reveal>
            <div className="mx-auto max-w-3xl border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12" data-testid="plan-questions">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-navy">Tell us about your trip</h2>
                <button onClick={() => setStep("path")} className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/50 hover:text-navy"><ArrowLeft size={12} /> Paths</button>
              </div>
              {path === "cruise" && (
                <p className="mt-3 flex items-center gap-2 border border-bermuda/40 bg-pink-soft/50 px-4 py-3 text-[13px] text-navy/70">
                  <Ship size={15} className="shrink-0 text-bermuda-dark" />
                  We'll only recommend what comfortably fits your hours ashore — with a return-to-ship reminder built in.
                </p>
              )}
              <div className="mt-8 grid gap-7">
                {questions.map((q) => (
                  <QuestionBlock key={q.id} q={q} value={answers[q.id]} onChange={setAnswer(q.id)} />
                ))}
              </div>
              <button
                onClick={() => { setStep("recs"); track("recommendations_ready"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                data-testid="plan-see-recs"
                className="mt-10 w-full bg-navy px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark"
              >
                See My Bermuda Recommendations
              </button>
            </div>
          </Reveal>
        )}

        {step === "recs" && (
          <div data-testid="plan-recs">
            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-serif text-3xl font-light text-navy">Your Bermuda recommendations</h2>
                <p className="mt-1 text-sm text-navy/55">
                  {scored.length} matches{path === "cruise" && cruiseHours ? ` · about ${Math.floor(cruiseHours)} hours ashore` : ""} · {savedIds.length} saved to your trip
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep("questions")} className="flex items-center gap-1.5 border border-navy/25 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory"><ArrowLeft size={12} /> Edit Answers</button>
                <button onClick={() => setStep("trip")} data-testid="plan-trip-btn" className="flex items-center gap-2 bg-bermuda px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                  <Heart size={12} /> My Trip ({savedIds.length})
                </button>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {scored.slice(0, 24).map((r, i) => (
                <RecCard key={r.id} rec={r} index={i} answers={answers} saved={savedIds.includes(r.id)} onToggle={() => toggleSave(r.id)} />
              ))}
            </div>
          </div>
        )}

        {step === "trip" && (
          <Reveal>
            <div className="mx-auto max-w-3xl" data-testid="plan-trip">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-serif text-3xl font-light text-navy">My itinerary</h2>
                <button onClick={() => setStep("recs")} className="flex items-center gap-1.5 border border-navy/25 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory"><ArrowLeft size={12} /> Recommendations</button>
              </div>
              {saved.length === 0 ? (
                <p className="mt-6 border border-dashed border-navy/20 bg-sand-light/60 p-8 text-center text-sm text-navy/55" data-testid="trip-empty">
                  Nothing saved yet — head back to your recommendations and tap "Add to My Trip" on the places you love.
                </p>
              ) : (
                <>
                  <div className="mt-8 flex items-center gap-1.5" data-testid="trip-view-toggle">
                    {[["list", "List View"], ["calendar", "Calendar View"], ["map", "Map View"]].map(([v, label]) => (
                      <button key={v} onClick={() => setTripView(v)} data-testid={`trip-view-${v}`} className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${tripView === v ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy/50 hover:border-navy/50 hover:text-navy"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {tripView === "map" ? (
                    <div className="mt-5" data-testid="trip-map-view">
                      <TripMap itinerary={itinerary} saved={saved} />
                    </div>
                  ) : tripView === "calendar" ? (
                    <div className="mt-5" data-testid="trip-calendar">
                      {weeks.length > 1 && (
                        <div className="mb-4 flex flex-wrap gap-1.5">
                          {weeks.map((_, i) => (
                            <button key={i} onClick={() => setWeekTab(i)} data-testid={`trip-week-tab-${i}`} className={`border px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${weekTab === i ? "border-bermuda-dark bg-bermuda/15 text-bermuda-dark" : "border-navy/15 text-navy/50 hover:border-navy/40"}`}>
                              Days {i * 7 + 1}–{Math.min((i + 1) * 7, itinerary.length)}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="grid gap-2 sm:grid-cols-4 lg:grid-cols-7">
                        {(weeks[Math.min(weekTab, weeks.length - 1)] || []).map((d) => (
                          <div key={d.day} className="min-h-28 border border-navy/10 bg-white p-2.5" data-testid={`trip-cal-day-${d.day}`}>
                            <p className="text-[9px] font-bold uppercase tracking-wider text-navy/45">
                              Day {d.day}{answers.arrival ? ` · ${new Date(new Date(answers.arrival).getTime() + (d.day - 1) * 86400000).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                            </p>
                            <div className="mt-2 space-y-1.5">
                              {d.slots.length === 0 ? (
                                <p className="text-[10px] italic text-navy/30">Free to wander</p>
                              ) : (
                                d.slots.map((s, i) => (
                                  <p key={i} title={s.label} className={`truncate px-1.5 py-1 text-[10px] leading-tight ${s.cruiseReturn ? "bg-bermuda/25 font-semibold text-bermuda-dark" : "bg-sand-light text-navy/70"}`}>
                                    <span className="font-semibold">{s.time}</span> {s.label}
                                  </p>
                                ))
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-[11px] text-navy/45">Switch to List View to edit, remove or send items to your calendar.</p>
                    </div>
                  ) : (
                    <div className="mt-5 space-y-6">
                      {itinerary.filter((d) => d.slots.length > 0).map((d) => (
                        <div key={d.day} className="border border-navy/10 bg-white" data-testid={`trip-day-${d.day}`}>
                          <div className="border-b border-navy/10 bg-sand-light/70 px-6 py-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-navy/60">Day {d.day}{answers.arrival ? ` · ${new Date(new Date(answers.arrival).getTime() + (d.day - 1) * 86400000).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}` : ""}</p>
                          </div>
                          <ul className="divide-y divide-navy/8">
                            {d.slots.map((s, i) => (
                              <li key={i} className={`flex flex-wrap items-center justify-between gap-3 px-6 py-4 ${s.cruiseReturn ? "bg-pink-soft/50" : ""}`} data-testid={`trip-item-${d.day}-${i}`}>
                                <div className="flex items-center gap-4">
                                  <span className={`w-24 shrink-0 text-[10px] font-bold uppercase tracking-wider ${s.cruiseReturn ? "text-bermuda-dark" : "text-navy/50"}`}>{s.custom ? s.time : s.time}</span>
                                  <div>
                                    <p className="text-sm font-medium text-navy">{s.label}</p>
                                    {s.duration ? <p className="text-[11px] text-navy/45">~{s.duration} hours</p> : null}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {s.recId && (
                                    <>
                                      <Link to={calendarLink(saved.find((r) => r.id === s.recId) || {}, answers, saved.find((r) => r.id === s.recId)?.category === "dining" ? "dining" : "activity")} data-testid={`trip-cal-${d.day}-${i}`} className="flex items-center gap-1.5 border border-navy/20 px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy/70 transition-colors hover:bg-navy hover:text-ivory">
                                        <CalendarPlus size={11} /> Calendar
                                      </Link>
                                      <button onClick={() => toggleSave(s.recId)} data-testid={`trip-remove-${d.day}-${i}`} className="border border-navy/20 px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy/50 transition-colors hover:border-bermuda-dark hover:text-bermuda-dark">
                                        Remove
                                      </button>
                                    </>
                                  )}
                                  {s.cruiseReturn && (
                                    <Link to={`/calendar?type=all-aboard${answers.arrival ? `&date=${answers.arrival}` : ""}${answers.allAboard ? `&start=${answers.allAboard}` : ""}${answers.ship ? `&title=${encodeURIComponent("All Aboard - Return to " + answers.ship)}` : ""}`} data-testid="trip-allaboard-cal" className="flex items-center gap-1.5 bg-bermuda px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                                      <Ship size={11} /> All-Aboard Reminder
                                    </Link>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="mt-8 border border-navy/10 bg-white p-6" data-testid="trip-save-panel">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl text-navy">Save your trip</h3>
                    <p className="mt-1 text-[13px] text-navy/55">Get a personal link so you — or your travel companions — can come back any time.</p>
                  </div>
                  <button onClick={saveTrip} disabled={saving || saved.length === 0} data-testid="trip-save-btn" className="bg-navy px-6 py-3.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark disabled:opacity-60">
                    {saving ? "Saving…" : tripCode ? "Update My Trip" : "Save My Trip"}
                  </button>
                </div>
                {tripLink && (
                  <div className="mt-4 flex flex-wrap items-center gap-3 border border-sage/40 bg-sage/10 px-4 py-3" data-testid="trip-code">
                    <span className="text-[12px] text-navy/70">Your trip link:</span>
                    <span className="font-mono text-[12px] font-bold text-navy">{tripLink}</span>
                    <button
                      onClick={async () => { try { await navigator.clipboard.writeText(tripLink); toast.success("Link copied."); } catch { toast.error("Copy failed."); } }}
                      data-testid="trip-copy-btn"
                      className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-bermuda-dark hover:text-navy"
                    >
                      <ClipboardCopy size={12} /> Copy
                    </button>
                  </div>
                )}
                {tripLink && (
                  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="trip-share">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-navy/50">Share with your travel group:</span>
                    <button onClick={shareTripNative} data-testid="trip-share-native" className="flex items-center gap-1.5 border border-navy/25 px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                      <Share2 size={11} /> Share
                    </button>
                    <a href={whatsappTripUrl} target="_blank" rel="noopener noreferrer" data-testid="trip-share-whatsapp" className="flex items-center gap-1.5 border border-navy/25 px-3 py-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                      <MessageCircle size={11} /> WhatsApp
                    </a>
                  </div>
                )}
              </div>

              <button onClick={() => setStep("concierge")} data-testid="plan-concierge-btn" className="mt-8 flex w-full items-center justify-center gap-2.5 bg-navy px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-bermuda-dark">
                <Sparkles size={14} /> Continue — Get Concierge Help
              </button>
            </div>
          </Reveal>
        )}

        {step === "concierge" && (
          <Reveal>
            <div className="mx-auto max-w-3xl border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12" data-testid="plan-concierge">
              {done ? (
                <div className="py-6 text-center" data-testid="concierge-success">
                  <Check size={36} strokeWidth={1.4} className="mx-auto text-sage" />
                  <h3 className="mt-4 font-serif text-3xl text-navy">Request received.</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-navy/60">
                    Our local team has your trip plan and your wishlist. We'll reply to <strong className="text-navy">{concierge.email}</strong> personally — usually within a day.
                  </p>
                  <div className="mt-7 text-left">
                    <GuideUpsell path={path} />
                  </div>
                  <Link to="/guest-tools" className="mt-6 inline-block border border-navy/25 px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                    Explore Free Guest Tools
                  </Link>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="font-serif text-2xl text-navy">Would you like us to help arrange it?</h2>
                    <button onClick={() => setStep("trip")} className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy/50 hover:text-navy"><ArrowLeft size={12} /> Itinerary</button>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-navy/60">
                    {saved.length} {saved.length === 1 ? "place" : "places"} in your plan. Tick anything you'd like our local team to arrange and we'll take it from there.
                  </p>
                  <div className="mt-6">
                    <GuideUpsell path={path} />
                  </div>
                  <form onSubmit={sendConcierge} className="mt-7 grid gap-6" data-testid="concierge-form">
                    <div className="flex flex-wrap gap-2">
                      {CONCIERGE_SERVICES.map((s) => (
                        <Chip
                          key={s}
                          testid={`concierge-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          active={concierge.services.includes(s)}
                          onClick={() => setConcierge((c) => ({ ...c, services: c.services.includes(s) ? c.services.filter((x) => x !== s) : [...c.services, s] }))}
                        >
                          {s}
                        </Chip>
                      ))}
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Your Name" required><Input data-testid="concierge-name" required value={concierge.name} onChange={(e) => setConcierge({ ...concierge, name: e.target.value })} /></Field>
                      <Field label="Email" required><Input data-testid="concierge-email" required type="email" value={concierge.email} onChange={(e) => setConcierge({ ...concierge, email: e.target.value })} /></Field>
                    </div>
                    <Field label="Anything else?"><Textarea data-testid="concierge-notes" rows={3} value={concierge.notes} onChange={(e) => setConcierge({ ...concierge, notes: e.target.value })} placeholder="Dates, details, special requests…" /></Field>
                    <SubmitButton loading={sending} testid="concierge-submit">Send My Request to The Bermuda Co-Host</SubmitButton>
                  </form>
                </>
              )}
            </div>
          </Reveal>
        )}

        {step === "path" && (
          <Reveal delay={0.2}>
            <div className="mx-auto mt-14 max-w-4xl border border-navy/10 bg-white p-8 text-center" data-testid="plan-guides-crosslink">
              <BookOpen size={20} strokeWidth={1.4} className="mx-auto text-bermuda-dark" />
              <p className="mt-3 text-sm leading-relaxed text-navy/60">
                Prefer to plan offline? Each path has a matching fillable PDF guide in our{" "}
                <Link to="/guides" data-testid="plan-guides-link" className="font-medium text-bermuda-dark underline underline-offset-4 hover:text-navy">Digital Guides library</Link> — $20 each, instant download.
              </p>
            </div>
          </Reveal>
        )}
      </section>

      <CtaBand title="Staying at one of our managed properties?" copy="Guests at Bermuda Co-Host homes get personal concierge support included — message us any time." />
    </main>
  );
}
